import clsx from 'clsx'
import React, { useCallback, useEffect, useRef } from 'react'
import { Portal } from '../portal'
import { useFocusTrap } from './useFocusTrap'

const rootStyle = {
  position: 'fixed',
  inset: 0,
  overflow: 'auto',
  pointerEvents: 'auto',
  zIndex: 9999
}

const backdropStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '100%',
  userSelect: 'none',
  pointerEvents: 'auto',
  background: 'rgba(47, 47, 47, 0.23)',
}

const dialogStyle = {
  background: '#FFF',
  borderRadius: '6px',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  minWidth: 500,
  maxWidth: 700,
  padding: '36px 60px 24px 60px',
  userSelect: 'text',
  pointerEvents: 'all',
  outline: 'none',
}

export const ModalHeader = ({ className, children }) => (
  <div className={clsx('dome-d-flex dome-flex-column dome-gap12', className)}>{children}</div>
)

export const ModalBody = ({ children, className }) => (
  <div className={clsx('dome-d-flex dome-flex-column', className)}>{children}</div>
)

export const ModalFooter = ({ children, className }) => (
  <div className={clsx('dome-d-flex dome-justify-between', className)}>{children}</div>
)

export const Modal = ({
  isOpen,
  children,
  onClose,
  className,
  autoFocus = false,
  focusLock = true,
  returnFocus = true,
  closeOnOutsideClick = true,
  container,
}) => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const modalRef = useFocusTrap({ isActive: isOpen, autoFocus, focusLock, returnFocus })

  useEffect(() => {
    const modalEl = modalRef.current
    if (!isOpen || !modalEl) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current(e)
    }
    modalEl.addEventListener('keydown', handleKeyDown)
    return () => modalEl.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    document.body.classList.toggle('overlay-activated', isOpen)
    return () => document.body.classList.remove('overlay-activated')
  }, [isOpen])

  const handleBackdropClick = useCallback(
    (e) => {
      if (closeOnOutsideClick && e.target === e.currentTarget) onCloseRef.current(e)
    },
    [closeOnOutsideClick]
  )

  if (!isOpen) return null

  return (
    <Portal container={container}>
      <div style={rootStyle}>
        <div style={backdropStyle} onClick={handleBackdropClick}>
          <div
            ref={modalRef}
            style={dialogStyle}
            className={clsx('dome-d-flex dome-flex-column dome-gap24', className)}
            role="dialog"
            aria-modal="true"
            aria-label="dialog"
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  )
}
